class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.drawerNav = document.querySelector('.wt-drawer--nav');
    this.isQuickSearch = this.dataset.quickSearch === "true";
    this.panel = this.querySelector(".quick-search");
    this.classDrawerActive = this.isQuickSearch
      ? `${this.drawerClass}--open`
      : `${this.drawerClass}--active`;
    this.pageOverlayClass = this.isQuickSearch ? "quick-search" : "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.drawerNavAnimationOffClass = 'wt-drawer--off-animations';
    this.body = document.body;
    this.activeOptionIndex = -1;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    // search stuff
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.statusElement = this.querySelector(".predictive-search-status");
    this.searchProperties = this.dataset.searchProperties;

    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.emptyAnnouncement = this.querySelector(".search-empty");
    this.searchResultTop = this.querySelector(".search-result-top");

    this.isVisibleClearButton = false;
    this.metricsFrame = null;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);

    this.isPredictiveSearchAvailable = this.hidePredictiveSearch();
    this.isPredictiveSearchEnabled = this.dataset.enablePredictiveSearch === 'true';
    this.showSuggestOnNoResults = this.dataset.showSuggestOnNoResults === 'true';

    // Listeners on document/window outlive this element's DOM, so they are tied to its lifecycle.
    this.globalListeners = new AbortController();

    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];

    this.suggestionMenuContent = this.querySelector(".search-result-top").cloneNode(true);
    this.timeOut = null;
    this.init();
  }

  disconnectedCallback() {
    this.globalListeners.abort();
    this.abortController?.abort();
    clearTimeout(this.timeOut);
    clearTimeout(this.metricsResetTimer);
    if (this.metricsFrame) cancelAnimationFrame(this.metricsFrame);
  }


  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  // fixOverlayIssue() {
  //     const body = this.body;
  //
  //     if(body.classList.contains(this.activeOverlayBodyClass)) {
  //         const offsetTop = -parseInt(body.style.top);
  //         body.style.position = 'initial';
  //         body.style.top = 'initial';
  //         body.style.left = 'initial';
  //         window.scrollTo(0, offsetTop);
  //
  //     } else {
  //         // body.style.top = -document.documentElement.scrollTop+'px';
  //         // body.style.left = '0px';
  //         // body.style.position = 'fixed';
  //     }
  // }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(this.toggleTabindexElements, "-1");
      setTabindex([this.mainTrigger], "0");
      this.isOpen = false;
      setTimeout(() => {
          this.mainTrigger.focus();
        }, 0);
    } else {
      this.setAttribute("open", "");
      setTabindex(this.toggleTabindexElements, "0");
      setTabindex([this.mainTrigger], "-1");
      this.input.focus();
      this.input.setSelectionRange(
        this.input.value.length,
        this.input.value.length,
      );
      this.isOpen = true;
    }
  }

  // Must run before the body class clamps the rows: the collapse transition needs their expanded height.
  publishCollapsingRowHeights() {
    const header = this.closest(".wt-header");
    if (!header) return;
    header.querySelectorAll(".wt-header__logo-wrapper, .wt-header__panel").forEach((row) => {
      row.style.setProperty("--quick-search-collapse-row-height", `${row.getBoundingClientRect().height}px`);
    });
  }

  toggleDrawerClasses() {
    const opening = !this.body.classList.contains(this.activeOverlayBodyClass);
    if (opening && this.isQuickSearch) this.publishCollapsingRowHeights();

    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.drawerNav?.classList.toggle(this.drawerNavAnimationOffClass);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    if (this.isQuickSearch) {
      clearTimeout(this.metricsResetTimer);
      if (opening) {
        this.updatePanelMetrics();
        this.setPanelState(this.panelStateForQuery());
        
        if (document.activeElement !== this.input) this.input.focus();
      } else {
        this.setPanelState("hidden");
        this.metricsResetTimer = setTimeout(() => this.clearPanelMetrics(), this.panelFadeDuration());
      }
    }

    if (opening) {
      this.armCascade(this.drawer);
    }
  }

  armCascade(root) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = root.querySelectorAll("[data-cascade] .scroll-trigger");
    if (items.length === 0) return;

    let idx = 0;
    items.forEach((item) => {
      if (item.classList.contains("search-result__title")) idx = 0;
      item.classList.add("scroll-trigger--offscreen");
      item.style.setProperty("--animation-order", idx);
      idx += 1;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        items.forEach((item) =>
          item.classList.remove("scroll-trigger--offscreen"),
        );
      });
    });
  }

  panelFadeDuration() {
    const seconds = parseFloat(getComputedStyle(this.panel).transitionDuration);
    return Number.isFinite(seconds) ? seconds * 1000 : 0;
  }

  clearPanelMetrics() {
    if (!this.panel || this.isOpen) return;
    ["--quick-search-bleed-left", "--quick-search-bleed-right", "--quick-search-panel-offset", "--quick-search-panel-top"].forEach(
      (property) => this.panel.style.removeProperty(property),
    );
  }

  updatePanelMetrics() {
    if (!this.panel) return;

    /*
       The panel sits inside the field's box but spans the full page width, so it needs the field's
       distance to each page edge, plus where the header row ends.
    */
    const anchor = this.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;

    // Page coordinates, not window coordinates. The panel is absolutely positioned, so measuring it
    // from a sideways-scrolled window would make it stretch the document and latch the scrollbar on.
    const scrolled = window.scrollX;

    // The panel is absolutely positioned, so it resolves against this element's PADDING box
    // while getBoundingClientRect() reports the border box. Ignoring the field's own 1px
    // border left a 1px strip of the page showing down each edge of a full-width panel.
    const frame = getComputedStyle(this);
    const borderLeft = parseFloat(frame.borderLeftWidth) || 0;
    const borderRight = parseFloat(frame.borderRightWidth) || 0;
    const borderBottom = parseFloat(frame.borderBottomWidth) || 0;

    // Sub-pixel values kept as-is: rounding the field's fractional offsets reopened the gap.
    this.panel.style.setProperty(
      "--quick-search-bleed-left",
      `${Math.max(0, anchor.left + scrolled + borderLeft).toFixed(2)}px`,
    );
    this.panel.style.setProperty(
      "--quick-search-bleed-right",
      `${Math.max(0, viewport - anchor.right - scrolled + borderRight).toFixed(2)}px`,
    );

    /*
       The panel opens at the bottom of the header's top row, not at the bottom of the field — that is
       where the design puts it (Figma: panel top 112.19.
    */
    // top: 100% puts the panel at the padding box's bottom, which is inside the border.
    const fieldBottom = Math.max(0, anchor.bottom - borderBottom);
    const row = this.closest(".wt-header__body");
    const panelTop = row
      ? Math.max(fieldBottom, row.getBoundingClientRect().bottom)
      : fieldBottom;

    this.panel.style.setProperty("--quick-search-panel-offset", `${(panelTop - fieldBottom).toFixed(2)}px`);
    this.panel.style.setProperty("--quick-search-panel-top", `${panelTop.toFixed(2)}px`);
  }

  // Below this the engine does not search, so the panel keeps its popular-searches state
  // instead of opening empty.
  minQueryLength() {
    return Number(this.dataset.quickSearchMinChars) || 2;
  }

  panelStateForQuery() {
    return this.getQuery().length >= this.minQueryLength() ? "results" : "empty";
  }

  hasEmptyStateContent() {
    return Boolean(this.querySelector(".predictive-search-status .quick-search__section"));
  }

  setPanelState(state) {
    if (!this.panel) return;

    const resolved =
      state === "empty" && !this.hasEmptyStateContent() ? "hidden" : state;

    this.panel.dataset.state = resolved;

    // The field's box moves while the mobile drawer opens, so the offsets the panel is
    // positioned from have to be re-read whenever the panel is (re)shown, not just on open.
    if (resolved !== "hidden") this.updatePanelMetrics();

    if (resolved !== "results") {
      this.clearActiveOption();
    }
  }

  options() {
    if (!this.panel) return [];
    return Array.from(this.panel.querySelectorAll('[role="option"]'));
  }

  clearActiveOption() {
    this.options().forEach((option) => option.setAttribute("aria-selected", "false"));
    this.activeOptionIndex = -1;
    this.input?.removeAttribute("aria-activedescendant");
  }

  moveActiveOption(step) {
    const options = this.options();
    if (options.length === 0) return;

    const next = this.activeOptionIndex + step;
    this.activeOptionIndex =
      next < 0 ? options.length - 1 : next >= options.length ? 0 : next;

    options.forEach((option, index) => {
      const isActive = index === this.activeOptionIndex;
      option.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        option.scrollIntoView({ block: "nearest" });
        if (option.id) this.input?.setAttribute("aria-activedescendant", option.id);
      }
    });
  }

  activeOptionLink() {
    const option = this.options()[this.activeOptionIndex];
    if (!option) return null;
    return option.matches("a[href]") ? option : option.querySelector("a[href]");
  }

  initQuickSearch() {
    const open = () => {
      if (this.hasAttribute("open")) return;
      this.toggleDrawerClasses();
    };

    this.input.addEventListener("focus", open);
    this.input.addEventListener("click", open);

    // With the Searchspring autocomplete the theme runs no fetch of its own, so nothing
    // else moves the panel out of its empty state when the shopper starts typing.
    if (!this.isPredictiveSearchEnabled) {
      this.input.addEventListener("input", () => {
        this.setPanelState(this.panelStateForQuery());
      });
    }

    this.input.addEventListener("keydown", (e) => {
      if (!this.isOpen) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.moveActiveOption(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.moveActiveOption(-1);
      } else if (e.key === "Enter") {
        const link = this.activeOptionLink();
        if (link) {
          e.preventDefault();
          link.click();
        }
      }
    });

    const signal = this.globalListeners.signal;

    document.addEventListener(
      "click",
      (e) => {
        if (this.isOpen && !this.contains(e.target) && !e.target.closest(this.triggerQuery)) {
          this.toggleDrawerClasses();
        }
      },
      { signal },
    );

    // Resize is throttled per frame rather than trailing-debounced: a panel still sized for the old
    // viewport is a horizontal scrollbar on the whole page for as long as it stays stale.
    const reposition = () => {
      if (this.metricsFrame) return;
      this.metricsFrame = requestAnimationFrame(() => {
        this.metricsFrame = null;
        if (this.isOpen) this.updatePanelMetrics();
      });
    };

    window.addEventListener("resize", reposition, { signal });
    window.addEventListener("scroll", debounce(reposition, 100), { passive: true, signal });
  }

  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    if (this.isQuickSearch) {
      this.initQuickSearch();
      this.publishCollapsingRowHeights();
    }

    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.input.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });
  }

  hidePredictiveSearch() {
    // when predictive search is not available, we need to hide the predictive search results
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      const featuresData = JSON.parse(featuresTag.textContent);
      if (featuresData.predictiveSearch === false) {
        return false;
      }
    }
    return true;
  }

  // search stuff
  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    if (this.isPredictiveSearchAvailable && this.isPredictiveSearchEnabled) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
        { signal: this.globalListeners.signal },
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=10&${encodeURIComponent("resources[options][fields]")}=${encodeURIComponent(this.searchProperties)}&section_id=predictive-search`,
      { signal: this.abortController.signal },
    )
      .then((response) => {
        if (!response.ok) {
          let error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
    this.setPanelState("loading");
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");

    // No fallback to statusElement itself: writing textContent there would wipe the popular-searches panel.
    const textTarget = this.statusElement.querySelector(".predictive-search-status__text");
    if (textTarget) textTarget.textContent = statusText;

    this.timeOut = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    if (this.showSuggestOnNoResults) {
      const hasResults = this.predictiveSearchResults.querySelector(
        ".search-result-products, .search-result-pages",
      );
      if (!hasResults) {
        this.predictiveSearchResults.appendChild(
          this.suggestionMenuContent.cloneNode(true),
        );
      }
    }

    this.setAttribute("results", true);
    this.setPanelState("results");

    this.setLiveRegionResults();

    this.armCascade(this.predictiveSearchResults);
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  clearResults() {
    this.input.value = "";
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    // Swap only the suggestions panel; the sibling live-region div has to survive for setLiveRegionText.
    const staleMenu = this.statusElement.querySelector(".search-result-top");
    const freshMenu = this.suggestionMenuContent.cloneNode(true);
    if (staleMenu) staleMenu.replaceWith(freshMenu);
    else this.statusElement.prepend(freshMenu);
    const liveRegion = this.statusElement.querySelector(".predictive-search-status__text");
    if (liveRegion) liveRegion.textContent = "";
    this.setPanelState("empty");

    clearTimeout(this.timeOut);
    setTimeout(() => {
      this.statusElement.removeAttribute("aria-hidden");
      this.armCascade(this.statusElement);
    }, 300);
  }
}

customElements.define("search-drawer", DrawerSearchSection);
